import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { adminInput, AdminField, SaveBar } from "./fields";
import { PROJECT_LOCATIONS, PROJECT_TYPES } from "@/data/content";

const EMPTY = {
  name: "", slug: "", location: "Tumakuru", category: "Independent Homes", floors: "",
  plot_size: "", built_up_area: "", completion_year: "", status: "Completed",
  description: "", client_vision: "", challenge: "", approach: "", materials: "",
  images: "", before_image: "", after_image: "", featured: false, sample: false,
};

const toForm = (p) => ({ ...EMPTY, ...p, images: (p.images || []).join("\n"), before_image: p.before_image || "", after_image: p.after_image || "" });
const toPayload = (f) => ({
  ...f,
  slug: f.slug || null,
  images: f.images.split("\n").map((s) => s.trim()).filter(Boolean),
  before_image: f.before_image || null,
  after_image: f.after_image || null,
});

export const ProjectsTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | project id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/projects", toPayload(form));
      else await api.put(`/admin/projects/${editing}`, toPayload(form));
      setEditing(null);
      setForm(EMPTY);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    load();
  };

  if (items === null) return <p className="text-sm text-charcoal/50">Loading…</p>;

  if (editing !== null) {
    return (
      <form onSubmit={save} className="max-w-2xl space-y-4 rounded-2xl border border-charcoal/10 bg-white p-7" data-testid="project-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <AdminField label="Name *"><input required className={adminInput} data-testid="project-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></AdminField>
          <AdminField label="Slug"><input className={adminInput} value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated" /></AdminField>
          <AdminField label="Location">
            <select className={adminInput} value={form.location} onChange={(e) => set("location", e.target.value)}>
              {PROJECT_LOCATIONS.filter((l) => l !== "All").map((l) => <option key={l}>{l}</option>)}
            </select>
          </AdminField>
          <AdminField label="Category">
            <select className={adminInput} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {PROJECT_TYPES.filter((tp) => tp !== "All").map((tp) => <option key={tp}>{tp}</option>)}
            </select>
          </AdminField>
          <AdminField label="Floors"><input className={adminInput} value={form.floors} onChange={(e) => set("floors", e.target.value)} placeholder="e.g. G+1" /></AdminField>
          <AdminField label="Status">
            <select className={adminInput} value={form.status} onChange={(e) => set("status", e.target.value)}>
              {["Completed", "Under Construction", "Upcoming"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </AdminField>
          <AdminField label="Completion year"><input className={adminInput} value={form.completion_year} onChange={(e) => set("completion_year", e.target.value)} /></AdminField>
          <AdminField label="Plot size"><input className={adminInput} value={form.plot_size} onChange={(e) => set("plot_size", e.target.value)} /></AdminField>
          <AdminField label="Built-up area"><input className={adminInput} value={form.built_up_area} onChange={(e) => set("built_up_area", e.target.value)} /></AdminField>
        </div>
        <AdminField label="Description"><textarea className={`${adminInput} min-h-[70px]`} value={form.description} onChange={(e) => set("description", e.target.value)} /></AdminField>
        <AdminField label="Client vision"><textarea className={`${adminInput} min-h-[60px]`} value={form.client_vision} onChange={(e) => set("client_vision", e.target.value)} /></AdminField>
        <AdminField label="Challenge"><textarea className={`${adminInput} min-h-[60px]`} value={form.challenge} onChange={(e) => set("challenge", e.target.value)} /></AdminField>
        <AdminField label="D & N approach"><textarea className={`${adminInput} min-h-[60px]`} value={form.approach} onChange={(e) => set("approach", e.target.value)} /></AdminField>
        <AdminField label="Materials & specifications"><textarea className={`${adminInput} min-h-[60px]`} value={form.materials} onChange={(e) => set("materials", e.target.value)} /></AdminField>
        <AdminField label="Image URLs (one per line)">
          <textarea className={`${adminInput} min-h-[90px] font-mono text-xs`} data-testid="project-images-input" value={form.images} onChange={(e) => set("images", e.target.value)} />
        </AdminField>
        <div className="grid gap-4 sm:grid-cols-2">
          <AdminField label="Before image URL"><input className={adminInput} value={form.before_image} onChange={(e) => set("before_image", e.target.value)} /></AdminField>
          <AdminField label="After image URL"><input className={adminInput} value={form.after_image} onChange={(e) => set("after_image", e.target.value)} /></AdminField>
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-charcoal/70">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="project-featured-checkbox" /> Featured on homepage
          </label>
          <label className="flex items-center gap-2 text-sm text-charcoal/70">
            <input type="checkbox" checked={form.sample} onChange={(e) => set("sample", e.target.checked)} /> Sample placeholder
          </label>
        </div>
        <SaveBar saving={saving} onCancel={() => { setEditing(null); setForm(EMPTY); }} />
      </form>
    );
  }

  return (
    <div data-testid="projects-admin-list">
      <button type="button" onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="project-new-button"
        className="mb-5 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-xs font-bold text-gold">
        <Plus className="h-4 w-4" /> New Project
      </button>
      {items.length === 0 ? (
        <p className="text-sm text-charcoal/50">No projects yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-xl border border-charcoal/10 bg-white px-5 py-4">
              {p.images?.[0] && <img src={p.images[0]} alt="" className="h-12 w-16 rounded-lg object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-charcoal">
                  {p.name} {p.sample && <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-gold-dark">sample</span>}
                </p>
                <p className="text-xs text-charcoal/50">{p.location} · {p.category} · {p.status}</p>
              </div>
              <button type="button" onClick={() => { setForm(toForm(p)); setEditing(p.id); }} data-testid={`project-edit-${p.slug}`}
                className="rounded-full border border-charcoal/20 p-2 text-charcoal/60 hover:text-charcoal" aria-label={`Edit ${p.name}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => remove(p)} data-testid={`project-delete-${p.slug}`}
                className="rounded-full border border-red-200 p-2 text-red-500 hover:text-red-700" aria-label={`Delete ${p.name}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
